const SESSION_CONFIG = {
  INACTIVITY_TIMEOUT: 15 * 60 * 1000,
  WARNING_TIMEOUT: 2 * 60 * 1000,
  MAX_SESSION_DURATION: 24 * 60 * 60 * 1000,
  WARNING_CHECK_INTERVAL: 30 * 1000,
  ACTIVITY_RESET_DELAY: 1000
};

const TEST_MODE = false;
if (TEST_MODE) {
  SESSION_CONFIG.INACTIVITY_TIMEOUT = 1 * 60 * 1000;
  SESSION_CONFIG.WARNING_TIMEOUT = 30 * 1000;
  SESSION_CONFIG.WARNING_CHECK_INTERVAL = 5 * 1000;
}

export class SessionManager {
  constructor(onSessionExpired) {
    this.onSessionExpired = onSessionExpired;
    this.state = {
      startTime: null,
      lastActivity: null,
      warningTimer: null,
      timeoutTimer: null,
      warningShown: false,
      isWarningModalOpen: false,
      countdownInterval: null,
      countdownTime: 0
    };
  }

  initialize() {
    const now = Date.now();
    this.state.startTime = now;
    this.state.lastActivity = now;
    this.state.warningShown = false;
    this.state.isWarningModalOpen = false;

    console.log('Session initialized at:', new Date(now).toISOString());
    console.log('Session config:', SESSION_CONFIG);

    this.startMonitoring();
    this.setupActivityListeners();
    this.updateStatusIndicator('active');
  }

  cleanup() {
    console.log('Cleaning up session');
    this.clearTimers();
    this.state = {
      startTime: null,
      lastActivity: null,
      warningTimer: null,
      timeoutTimer: null,
      warningShown: false,
      isWarningModalOpen: false,
      countdownInterval: null,
      countdownTime: 0
    };
    this.updateStatusIndicator('hidden');
  }

  startMonitoring() {
    this.clearTimers();

    console.log('Starting session monitoring with timeout:', SESSION_CONFIG.INACTIVITY_TIMEOUT / 1000, 'seconds');

    this.state.timeoutTimer = setTimeout(() => {
      console.log('Main session timeout triggered - signing out');
      this.forceExpire('Session expired due to inactivity');
    }, SESSION_CONFIG.INACTIVITY_TIMEOUT);

    this.state.warningTimer = setInterval(() => {
      this.checkTimeout();
    }, SESSION_CONFIG.WARNING_CHECK_INTERVAL);
  }

  resetActivity() {
    const now = Date.now();
    this.state.lastActivity = now;

    if (this.state.timeoutTimer) {
      clearTimeout(this.state.timeoutTimer);
      this.state.timeoutTimer = setTimeout(() => {
        console.log('Session timeout reached - signing out');
        this.forceExpire('Session expired due to inactivity');
      }, SESSION_CONFIG.INACTIVITY_TIMEOUT);
    }

    if (this.state.warningShown && !this.state.isWarningModalOpen) {
      this.hideWarning();
    }

    this.updateStatusIndicator('active');
  }

  setupActivityListeners() {
    let activityTimeout;
    const handleActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => {
        this.resetActivity();
      }, SESSION_CONFIG.ACTIVITY_RESET_DELAY);
    };

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    console.log('Activity listeners configured');
  }

  checkTimeout() {
    if (this.state.isWarningModalOpen) return;

    const now = Date.now();
    const timeSinceActivity = now - this.state.lastActivity;
    const timeUntilTimeout = SESSION_CONFIG.INACTIVITY_TIMEOUT - timeSinceActivity;

    console.log('Session check - time since activity:', Math.floor(timeSinceActivity / 1000), 's, time until timeout:', Math.floor(timeUntilTimeout / 1000), 's');

    const timeUntilWarning = timeUntilTimeout - SESSION_CONFIG.WARNING_TIMEOUT;
    if (timeUntilWarning <= 0) {
      this.updateStatusIndicator('warning');
    } else {
      this.updateStatusIndicator('active');
    }

    if (timeUntilTimeout <= SESSION_CONFIG.WARNING_TIMEOUT && !this.state.warningShown) {
      console.log('Showing session warning - time until timeout:', Math.floor(timeUntilTimeout / 1000), 's');
      this.showWarning();
    }

    const sessionDuration = now - this.state.startTime;
    if (sessionDuration >= SESSION_CONFIG.MAX_SESSION_DURATION) {
      console.log('Maximum session duration reached - signing out');
      this.updateStatusIndicator('expired');
      this.forceExpire('Session expired after 24 hours');
    }

    if (timeUntilTimeout <= 0) {
      console.log('Session timeout reached - signing out');
      this.updateStatusIndicator('expired');
      this.forceExpire('Session expired due to inactivity');
    }
  }

  showWarning() {
    if (this.state.isWarningModalOpen) return;

    console.log('Showing session timeout warning');
    this.state.warningShown = true;
    this.state.isWarningModalOpen = true;
    this.syncExtendLabel();

    const now = Date.now();
    const timeSinceActivity = now - this.state.lastActivity;
    const timeUntilTimeout = SESSION_CONFIG.INACTIVITY_TIMEOUT - timeSinceActivity;
    this.state.countdownTime = Math.max(0, Math.floor(timeUntilTimeout / 1000));

    const modal = document.getElementById('sessionTimeoutModal');
    if (modal) {
      modal.classList.remove('hidden');
      this.updateCountdownDisplay();
      this.setupModalListeners();

      // The countdown is derived from lastActivity rather than decremented on
      // its own. It used to tick down independently, so activity reset the
      // timeout timer while the countdown kept running — and an operator who
      // was actively working got signed out anyway.
      this.state.countdownInterval = setInterval(() => {
        const elapsed = Date.now() - this.state.lastActivity;
        const remaining = Math.ceil((SESSION_CONFIG.INACTIVITY_TIMEOUT - elapsed) / 1000);
        this.state.countdownTime = Math.max(0, remaining);
        this.updateCountdownDisplay();

        if (this.state.countdownTime <= 0) {
          this.hideWarning();
          this.forceExpire('Session expired due to inactivity');
        }
      }, 1000);
    }
  }

  hideWarning() {
    const modal = document.getElementById('sessionTimeoutModal');
    if (!modal) return;

    console.log('Hiding session timeout warning');
    this.state.warningShown = false;
    this.state.isWarningModalOpen = false;

    if (this.state.countdownInterval) {
      clearInterval(this.state.countdownInterval);
      this.state.countdownInterval = null;
    }

    modal.classList.add('hidden');
    this.updateStatusIndicator('active');
  }

  updateCountdownDisplay() {
    const countdownEl = document.getElementById('sessionCountdown');
    if (!countdownEl) return;

    const minutes = Math.floor(this.state.countdownTime / 60);
    const seconds = this.state.countdownTime % 60;
    countdownEl.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    if (this.state.countdownTime <= 30) {
      countdownEl.classList.add('text-error');
      countdownEl.classList.remove('text-warning');
    } else {
      countdownEl.classList.add('text-warning');
      countdownEl.classList.remove('text-error');
    }
  }

  setupModalListeners() {
    const extendBtn = document.getElementById('sessionExtendBtn');
    const signOutBtn = document.getElementById('sessionSignOutBtn');

    if (extendBtn) {
      const newExtendBtn = extendBtn.cloneNode(true);
      extendBtn.parentNode.replaceChild(newExtendBtn, extendBtn);
      newExtendBtn.addEventListener('click', () => this.extendSession());
    }

    if (signOutBtn) {
      const newSignOutBtn = signOutBtn.cloneNode(true);
      signOutBtn.parentNode.replaceChild(newSignOutBtn, signOutBtn);
      newSignOutBtn.addEventListener('click', () => {
        this.hideWarning();
        this.onSessionExpired('User signed out from warning modal');
      });
    }
  }

  /** Keep the extend button's label truthful about how long it grants. */
  syncExtendLabel() {
    const el = document.getElementById('sessionExtendLabel');
    if (!el) return;
    const mins = Math.round(SESSION_CONFIG.INACTIVITY_TIMEOUT / 60000);
    el.textContent = `Stay signed in (${mins} more minute${mins === 1 ? '' : 's'})`;
  }

  extendSession() {
    console.log('Extending session');
    this.hideWarning();
    this.resetActivity();
    this.state.warningShown = false;
    this.state.isWarningModalOpen = false;
    this.clearTimers();
    this.startMonitoring();
  }

  forceExpire(reason) {
    console.log('Force expire:', reason);
    this.hideWarning();
    this.cleanup();
    this.onSessionExpired(reason);
  }

  clearTimers() {
    if (this.state.timeoutTimer) {
      clearTimeout(this.state.timeoutTimer);
      this.state.timeoutTimer = null;
    }

    if (this.state.warningTimer) {
      clearInterval(this.state.warningTimer);
      this.state.warningTimer = null;
    }

    if (this.state.countdownInterval) {
      clearInterval(this.state.countdownInterval);
      this.state.countdownInterval = null;
    }
  }

  updateStatusIndicator(status) {
    const indicator = document.getElementById('sessionStatusIndicator');
    if (!indicator) return;

    indicator.className = 'absolute top-6 left-6 flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-xl z-20 transition-all duration-300';

    switch (status) {
      case 'active':
        indicator.classList.add('bg-success/10', 'text-success', 'border', 'border-success/20');
        indicator.innerHTML = `
          <div class="w-2 h-2 bg-success rounded-full animate-pulse"></div>
          <span>Session Active</span>
        `;
        break;
      case 'warning':
        indicator.classList.add('bg-warning/10', 'text-warning', 'border', 'border-warning/20');
        indicator.innerHTML = `
          <div class="w-2 h-2 bg-warning rounded-full animate-pulse"></div>
          <span>Session Warning</span>
        `;
        break;
      case 'expired':
        indicator.classList.add('bg-error/10', 'text-error', 'border', 'border-error/20');
        indicator.innerHTML = `
          <div class="w-2 h-2 bg-error rounded-full"></div>
          <span>Session Expired</span>
        `;
        break;
      default:
        indicator.classList.add('hidden');
    }
  }
}
